import { DocumentNode, NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { Box, Checkbox, Icon, Text, TextFieldSize, ToastType, useModalState } from '@bechtel/gds';
import {
  ListType,
  MetadataTableName,
  Note,
  NoteCommodityType,
  NoteInput,
  NoteType,
  PrimaryStatus,
  Question,
  SaveNotesInput,
  SaveNotesResult,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { ErrorMessage, Formik, FormikHelpers } from 'formik';
import { endsWith, find, forEach, get, includes, keys, lowerCase } from 'lodash';
import { FC, lazy, useEffect, useRef, useState } from 'react';
import { object, string } from 'yup';
import {
  CommodityFooter,
  ErrorRetry,
  MultiLingualText,
  NotesSkeleton,
  QuestionModalResponse,
  SelectGridModal,
  useAppUser,
  useLanguage,
} from '~/components';
import {
  GET_CABLE_BY_ID,
  GET_CABLE_VIEW_STATS,
  GET_EQUIPMENT_BY_ID,
  GET_EQUIPMENT_VIEW_STATS,
  GET_JUMPER_BY_ID,
  GET_JUMPER_VIEW_STATS,
  GET_NOTES_BY_COMMODITY,
  GET_RACEWAY_BY_ID,
  GET_RACEWAY_VIEW_STATS,
  SAVE_NOTES,
} from '~/gql';
import { useDate, useTableMetadataQuery, useToast } from '~/hooks';
import {
  ClearButtonStyled,
  CopyButtonStyled,
  HRStyled,
  NotesBoxStyled,
  TextAreaStyled,
  ValueCountStyled,
} from './styled';
import { NotesModalTypes, NotesProps, SaveNotesFormikState } from './types';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));

const validationSchema = object().shape({
  engineering: string().max(4000, 'Notes cannot exceed 4000 characters.').nullable(),
  construction: string().max(4000, 'Notes cannot exceed 4000 characters.').nullable(),
  cardRevisionQuestion: string().nullable(),
});

export const Notes: FC<NotesProps> = ({
  commodityId,
  commodityType,
  primaryStatus,
  canAddEngineeringNote,
  canAddConstructionNote,
}) => {
  const { showToast } = useToast();
  const { formatISODate, formatTime, formatLocaleDate } = useDate();
  const { close, visible, open } = useModalState(false);
  const setFieldValueRef = useRef<FormikHelpers<any>['setFieldValue'] | null>(null);
  const [notesModalType, setNotesModalType] = useState('');
  const { getMultiLingualTextByKey } = useLanguage();
  const [getNotesMetadata, { data: notesMetadata }] = useTableMetadataQuery();
  const appUser = useAppUser();
  const { isAlternateLanguageUsed } = useLanguage();
  const { userAccount } = useAppUser();

  useEffect(() => {
    if (!appUser.userAccount?.defaultProject?.id) return;
    getNotesMetadata({
      variables: {
        // All the notes commodity table has same language text
        name: MetadataTableName.CABLE_NOTE,
        projectId: appUser.userAccount?.defaultProject?.id,
      },
    });
  }, [appUser.userAccount?.defaultProject?.id, getNotesMetadata]);
  const entityText = isAlternateLanguageUsed
    ? get(notesMetadata, 'tableMetadata.alternateDescription', '')
    : get(notesMetadata, 'tableMetadata.description', '');

  const { data, loading, networkStatus, error, refetch } = useQuery(GET_NOTES_BY_COMMODITY, {
    variables: {
      commodityId,
      noteCommodityType: commodityType,
    },
    onCompleted: (data) => {
      const notes: Note[] = get(data, 'notesByCommodity', []);
      const engineeringNote = find(notes, (note) => NoteType[note.noteType] === NoteType.E);
      const constructionNote = find(notes, (note) => NoteType[note.noteType] === NoteType.C);
      const isCardRevisionRequired =
        primaryStatus && (primaryStatus == PrimaryStatus.J || primaryStatus == PrimaryStatus.C);

      const initialFormValues: SaveNotesFormikState = {
        engineering: engineeringNote?.note ? engineeringNote.note : '',
        construction: constructionNote?.note ? constructionNote.note : '',
        cardRevisionQuestion: isCardRevisionRequired ? Question.REPLY_YES : Question.NOT_ASKED,
      };
      setInitialValues(initialFormValues);
      formikValuesRef.current = initialFormValues;
    },
  });

  const notes: Note[] = get(data, 'notesByCommodity', []);
  const engineeringNote = find(notes, (note) => NoteType[note.noteType] === NoteType.E);
  const constructionNote = find(notes, (note) => NoteType[note.noteType] === NoteType.C);
  const isCardRevisionRequired =
    primaryStatus && (primaryStatus == PrimaryStatus.J || primaryStatus == PrimaryStatus.C);

  const initialFormValues: SaveNotesFormikState = {
    engineering: engineeringNote?.note ? engineeringNote.note : '',
    construction: constructionNote?.note ? constructionNote.note : '',
    cardRevisionQuestion: isCardRevisionRequired ? Question.REPLY_YES : Question.NOT_ASKED,
  };
  const [initialValues, setInitialValues] = useState<SaveNotesFormikState>(initialFormValues);
  const formikValuesRef = useRef<SaveNotesFormikState>(initialFormValues);

  const getQueryName = (): DocumentNode => {
    switch (commodityType) {
      case NoteCommodityType.Cable:
        return GET_CABLE_VIEW_STATS;
      case NoteCommodityType.Equipment:
        return GET_EQUIPMENT_VIEW_STATS;
      case NoteCommodityType.Raceway:
        return GET_RACEWAY_VIEW_STATS;
      case NoteCommodityType.Jumper:
        return GET_JUMPER_VIEW_STATS;
      case NoteCommodityType.CableFromTerm:
        return GET_CABLE_VIEW_STATS;
      case NoteCommodityType.CableToTerm:
        return GET_CABLE_VIEW_STATS;
      default:
        return GET_CABLE_VIEW_STATS;
    }
  };

  const getMainCommodityQueryName = (): DocumentNode => {
    switch (commodityType) {
      case NoteCommodityType.Cable:
        return GET_CABLE_BY_ID;
      case NoteCommodityType.Equipment:
        return GET_EQUIPMENT_BY_ID;
      case NoteCommodityType.Raceway:
        return GET_RACEWAY_BY_ID;
      case NoteCommodityType.Jumper:
        return GET_JUMPER_BY_ID;
      case NoteCommodityType.CableFromTerm:
        return GET_CABLE_BY_ID;
      case NoteCommodityType.CableToTerm:
        return GET_CABLE_BY_ID;
      default:
        return GET_CABLE_BY_ID;
    }
  };

  const [saveNotes, { data: saveNotesData, loading: saveNotesLoading }] = useMutation<
    { resolveBlock: SaveNotesResult },
    { saveNotesInput: SaveNotesInput }
  >(SAVE_NOTES, {
    update(cache) {
      cache.evict({ fieldName: `${lowerCase(commodityType)}ById` });
      cache.gc();
    },
    onCompleted: (data) => {
      if (get(data, 'saveNotes.notes')) {
        showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, [entityText, ''], ToastType.INFO);
        setInitialValues(formikValuesRef.current);
      }
    },
    onError: () => {
      showToast(UiTextEnum.ERROR_WHILE_SAVING_TOAST, [entityText], ToastType.WARNING);
    },
    refetchQueries: [
      {
        query: GET_NOTES_BY_COMMODITY,
        variables: {
          commodityId,
          noteCommodityType: commodityType,
        },
      },
      {
        query: getQueryName(),
        variables: {
          id: commodityId,
        },
      },
      {
        query: getMainCommodityQueryName(),
        variables: {
          id: commodityId,
        },
      },
    ],
  });

  const constructionNoteType = find(keys(NoteType), (key) => NoteType[key] == NoteType.C);
  const engineeringNoteType = find(keys(NoteType), (key) => NoteType[key] == NoteType.E);

  const selectItem = (data) => {
    if (data) {
      if (setFieldValueRef.current) {
        if (endsWith(notesModalType, '_ENGINEERING')) {
          setFieldValueRef?.current('engineering', data.note);
        } else {
          setFieldValueRef?.current('construction', data.note);
        }
      }
    }
    close();
  };

  const processNote = (note) => {
    // Regular expression to match ISO date and time format
    const isoDateRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/g;
    // Replace all occurrences of ISO date with formatted date
    const formattedNote = note?.replace(isoDateRegex, (match) => {
      return `${formatISODate(match)} ${formatTime(match)}`;
    });
    return formattedNote;
  };

  const saveHandler = async (values) => {
    const notesData: NoteInput[] = [];

    if (initialValues?.engineering !== values.engineering) {
      notesData.push({
        noteType: engineeringNoteType as NoteType,
        note: values.engineering,
      });
    }

    if (initialValues?.construction !== values.construction) {
      notesData.push({
        noteType: constructionNoteType as NoteType,
        note: values.construction,
      });
    }

    const saveNotesInput: SaveNotesInput = {
      noteCommodityType: commodityType,
      parentId: commodityId ? commodityId : '',
      notes: notesData,
      cardRevisionQuestion: values.cardRevisionQuestion ? values.cardRevisionQuestion : Question.REPLY_YES,
    };
    await saveNotes({
      variables: {
        saveNotesInput: saveNotesInput,
      },
    });
  };

  const handleQuestions = async (questionResponses: QuestionModalResponse[]) => {
    const formikValues = formikValuesRef.current;
    forEach(questionResponses, (questionResponse) => {
      if (questionResponse.messageKey == UiMessageKey.REVISED_INSTALLATION_CARD_AUTHORIZATION_FLAG_NOT_SET) {
        formikValues.cardRevisionQuestion = questionResponse.response;
      }
    });
    await saveHandler(formikValues);
  };

  const errorKeys: UiMessageKey[] = get(saveNotesData, 'saveNotes.errors') ?? [];

  if (loading || networkStatus === NetworkStatus.refetch) {
    return <NotesSkeleton />;
  }

  return (
    <>
      <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
        {!loading && error && <ErrorRetry refetch={refetch} />}
        {!loading && !error && (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={async (values) => {
              formikValuesRef.current = values;
              await saveHandler(values);
            }}
          >
            {({
              values,
              isValid,
              dirty,
              handleChange,
              handleSubmit,
              handleBlur,
              setFieldValue,
              resetForm,
              touched,
            }) => {
              setFieldValueRef.current = setFieldValue;
              return (
                <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                  <NotesBoxStyled>
                    <Box
                      flexDirection={{ sm: 'column' }}
                      pb={{ sm: '700' }}
                      px={{ sm: '700' }}
                      pt={{ sm: '500' }}
                      flexShrink={{ sm: 1 }}
                    >
                      <Box
                        pb={{ sm: '450' }}
                        pt={{ sm: '200' }}
                        css={`
                          ${({ theme }) => `border-bottom: 1px solid ${theme.colors.black};`}
                        `}
                        alignItems={{ sm: 'flex-start' }}
                      >
                        <MultiLingualText size="400" color="primary.700" weight="600">
                          Engineering Notes
                        </MultiLingualText>
                      </Box>
                      <Box mt={{ sm: '500' }} css={{ position: 'relative' }} flexDirection={{ sm: 'column' }}>
                        <TextAreaStyled
                          name="engineering"
                          onChange={handleChange}
                          value={processNote(values?.engineering)}
                          placeholder={getMultiLingualTextByKey(UiTextEnum.ENTER_COMMENTS)}
                          rows={userAccount?.tenantConfig?.isConstrNotesAllowed ? 5 : 15}
                          onBlur={handleBlur}
                          disabled={!canAddEngineeringNote}
                        />
                        {canAddEngineeringNote && (
                          <ValueCountStyled>
                            <Text color="grey.600" size="200">
                              {values?.engineering?.length}/4000
                            </Text>
                          </ValueCountStyled>
                        )}

                        <ErrorMessage name="engineering">
                          {(msg) => {
                            return (
                              <Box pt={{ sm: '300' }}>
                                <Text size="300" color="red.700">
                                  {msg}
                                </Text>
                              </Box>
                            );
                          }}
                        </ErrorMessage>
                      </Box>

                      <Box mt={{ sm: '400' }} flexShrink={{ sm: 1 }} justifyContent={{ sm: 'space-between' }}>
                        <Box flexGrow={{ sm: 1 }}>
                          {engineeringNote?.updatedTime && (
                            <>
                              <MultiLingualText color="grey.600" size="200" family="openSansCondensed">
                                Last Updated
                              </MultiLingualText>
                              <Text color="grey.600" size="200" family="openSansCondensed">
                                - {formatLocaleDate(engineeringNote.updatedTime)}
                              </Text>
                            </>
                          )}
                        </Box>
                        <Box>
                          <ClearButtonStyled
                            onClick={() => {
                              setFieldValue('engineering', '');
                              close();
                            }}
                            disabled={!canAddEngineeringNote}
                          >
                            <Icon icon="eraser" iconStyle="regular" />
                            <MultiLingualText weight="400" color="red.700" size="300">
                              Clear
                            </MultiLingualText>
                          </ClearButtonStyled>
                          <Box ml={{ sm: '400' }} flexShrink={{ sm: 0 }}>
                            <CopyButtonStyled
                              onClick={() => {
                                open();
                                setNotesModalType(
                                  // CableFromTerm and CableToTerm uses cable select list for copy functionality
                                  includes(
                                    [NoteCommodityType.CableFromTerm, NoteCommodityType.CableToTerm],
                                    commodityType
                                  )
                                    ? NotesModalTypes['CABLE_ENGINEERING']
                                    : NotesModalTypes[`${commodityType.toLocaleUpperCase()}_ENGINEERING`]
                                );
                              }}
                            >
                              <Icon icon="copy" iconStyle="regular" />
                              <MultiLingualText weight="400" size={300}>
                                Copy from...
                              </MultiLingualText>
                            </CopyButtonStyled>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <HRStyled />
                    {userAccount?.tenantConfig?.isConstrNotesAllowed && (
                      <>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          flexShrink={{ sm: 1 }}
                          px={{ sm: '700' }}
                          pt={{ sm: '600' }}
                          pb={{ sm: '700' }}
                        >
                          <Box
                            pb={{ sm: '450' }}
                            css={`
                              ${({ theme }) => `border-bottom: 1px solid ${theme.colors.black};`}
                            `}
                            alignItems={{ sm: 'flex-start' }}
                          >
                            <MultiLingualText size="400" color="primary.700" weight="600">
                              Construction Notes
                            </MultiLingualText>
                          </Box>
                          <Box mt={{ sm: '500' }} css={{ position: 'relative' }} flexDirection={{ sm: 'column' }}>
                            <TextAreaStyled
                              name="construction"
                              onChange={handleChange}
                              value={values?.construction}
                              placeholder={getMultiLingualTextByKey(UiTextEnum.ENTER_COMMENTS)}
                              rows={5}
                              onBlur={handleBlur}
                              disabled={!canAddConstructionNote}
                            />
                            {canAddConstructionNote && (
                              <ValueCountStyled>
                                <Text color="grey.600" size="200">
                                  {values?.construction?.length}/4000
                                </Text>
                              </ValueCountStyled>
                            )}

                            <ErrorMessage name="construction">
                              {(msg) => {
                                return (
                                  <Box pt={{ sm: '300' }}>
                                    <Text size="300" color="red.700">
                                      {msg}
                                    </Text>
                                  </Box>
                                );
                              }}
                            </ErrorMessage>
                          </Box>

                          <Box mt={{ sm: '400' }} flexShrink={{ sm: 1 }} justifyContent={{ sm: 'space-between' }}>
                            <Box flexGrow={{ sm: 1 }}>
                              {constructionNote?.updatedTime && (
                                <>
                                  <MultiLingualText color="grey.600" size="200" family="openSansCondensed">
                                    Last Updated
                                  </MultiLingualText>
                                  <Text color="grey.600" size="200" family="openSansCondensed">
                                    - {formatLocaleDate(constructionNote.updatedTime)}
                                  </Text>
                                </>
                              )}
                            </Box>
                            <Box>
                              <ClearButtonStyled
                                onClick={() => {
                                  setFieldValue('construction', '');
                                }}
                                disabled={!canAddConstructionNote}
                              >
                                <Icon icon="eraser" iconStyle="regular" />
                                <MultiLingualText weight="400" color="red.700" size="300">
                                  Clear
                                </MultiLingualText>
                              </ClearButtonStyled>
                              <Box ml={{ sm: '400' }} flexShrink={{ sm: 0 }}>
                                <CopyButtonStyled
                                  disabled={!canAddConstructionNote}
                                  onClick={() => {
                                    // CableFromTerm and CableToTerm uses cable select list for copy functionality
                                    setNotesModalType(
                                      includes(
                                        [NoteCommodityType.CableFromTerm, NoteCommodityType.CableToTerm],
                                        commodityType
                                      )
                                        ? NotesModalTypes['CABLE_CONSTRUCTION']
                                        : NotesModalTypes[`${commodityType.toLocaleUpperCase()}_CONSTRUCTION`]
                                    );
                                    open();
                                  }}
                                >
                                  <Icon icon="copy" iconStyle="regular" />
                                  <MultiLingualText weight="400" size="300">
                                    Copy from...
                                  </MultiLingualText>
                                </CopyButtonStyled>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        <HRStyled />
                      </>
                    )}

                    <Box
                      alignItems={{ sm: 'center' }}
                      px={{ sm: '700' }}
                      pt={{ sm: '600' }}
                      pb={{ sm: '700' }}
                      css={{ cursor: isCardRevisionRequired ? 'pointer' : 'default' }}
                      onClick={() => {
                        setFieldValue(
                          'cardRevisionQuestion',
                          primaryStatus == PrimaryStatus.J || primaryStatus == PrimaryStatus.C
                            ? values?.cardRevisionQuestion === Question.REPLY_YES
                              ? Question.REPLY_NO
                              : Question.REPLY_YES
                            : Question.NOT_ASKED
                        );
                      }}
                    >
                      <Box pr={{ sm: '500' }}>
                        <Checkbox
                          checked={values?.cardRevisionQuestion === Question.REPLY_YES}
                          size={TextFieldSize.MEDIUM}
                          disabled={!isCardRevisionRequired}
                        />
                      </Box>
                      <MultiLingualText size="300" color={isCardRevisionRequired ? 'primary' : 'grey.500'}>
                        Authorize revised installation card
                      </MultiLingualText>
                    </Box>
                  </NotesBoxStyled>
                  <CommodityFooter
                    saveHandler={() => handleSubmit()}
                    loading={saveNotesLoading}
                    disableSave={!dirty || !isValid || !touched}
                    disableCancel={!dirty || !touched}
                    cancelHandler={resetForm}
                  />
                </Box>
              );
            }}
          </Formik>
        )}
        {visible && (
          <SelectGridModal
            listName={notesModalType}
            title={getMultiLingualTextByKey(UiTextEnum.COPY_FROM)}
            close={close}
            selectItem={selectItem}
            listType={ListType?.NONE}
          ></SelectGridModal>
        )}
      </Box>
      {errorKeys && errorKeys.length > 0 && (
        <MessageModal onSubmit={handleQuestions} messageKeys={errorKeys}></MessageModal>
      )}
    </>
  );
};
